import { useCompanyRelativePath, useGetOGPUserInfo } from 'ogp/hooks';
import { useGetSupplierWorkTargets } from 'ogp/hooks/queryHooks/service-agreements/use-get-supplier-work-targets';
import { useCallback } from 'react';
import { BulkArchiveSidebar } from 'shared/components/bulk/bulk-shift-action/bulk-archive-sidebar';
import { BulkEditSidebar } from 'shared/components/bulk/bulk-shift-action/bulk-edit-sidebar';
import { WorkFormSidebar } from 'shared/components/work-forms/work-form-sidebar';
import { WorkFormCompanyInfo } from 'shared/components/work-forms/work-form.types';
import { RenderUnsettledUI } from 'shared/components';
import { useLocation, useNavigate } from 'react-router-dom';
import { paths } from 'ogp/paths';
import { BulkShiftCreationSidebar } from 'shared/components/bulk/bulk-shift-action/bulk-shift-creation-sidebar';
import { useIsHqImpersonation } from 'shared/hooks';
import { usePlanningContext } from './providers/planning-actions-provider';

export const PlanningSidebar = () => {
  const { selectedShiftIds, sidebarState, setSidebarState } = usePlanningContext();
  const userInfoQuery = useGetOGPUserInfo();
  const isCreate = sidebarState === 'create';
  const supplierWorkTargetsQuery = useGetSupplierWorkTargets(isCreate);
  const generatePath = useCompanyRelativePath();
  const navigate = useNavigate();
  const { search } = useLocation();
  const isHqAdmin = useIsHqImpersonation();

  const handleWorkFormSuccess = useCallback(
    (shiftId: string) => {
      const activeRoute = location.pathname.split('/').pop();
      const filterParams = Object.fromEntries(new URLSearchParams(search));
      const mergedParams = { ...filterParams, shiftId };
      const path = generatePath(paths.ShiftPlanning, undefined, mergedParams, activeRoute);
      navigate(path);
    },
    [search]
  );
  if (userInfoQuery.status !== 'success') {
    return <RenderUnsettledUI data={userInfoQuery} />;
  }

  if (isCreate && supplierWorkTargetsQuery.status !== 'success') {
    return <RenderUnsettledUI data={supplierWorkTargetsQuery} />;
  }

  const { data: userInfo } = userInfoQuery;
  const ownCompany: WorkFormCompanyInfo = {
    ...userInfo.userInfo.company,
    id: userInfo.userInfo.company.slug,
  };
  const borrowerCompanies: WorkFormCompanyInfo[] = (supplierWorkTargetsQuery.data ?? [])
    .filter((target) => target.companyId !== ownCompany.id)
    .map((target) => ({
      id: target.companyId,
      name: target.name,
      offersFreelanceWork: target.offersFreelanceWork,
      offersTempWork: target.offersTempWork,
      offersEmployeeWork: target.offersEmployeeWork,
    }));
  const selectBorrowerCompany = isCreate && borrowerCompanies.length > 0;
  const companies = selectBorrowerCompany ? [ownCompany, ...borrowerCompanies] : [ownCompany];

  return (
    <>
      <BulkEditSidebar
        selectedShiftIds={selectedShiftIds}
        isBulkSideBarOpen={sidebarState === 'bulk-edit'}
        setIsBarSideBarOpen={() => setSidebarState(null)}
      />
      <BulkArchiveSidebar
        selectedShiftIds={selectedShiftIds}
        isBulkArchiveBarOpen={sidebarState === 'bulk-archive'}
        setIsBarSideBarOpen={() => setSidebarState(null)}
      />
      {sidebarState === 'create' || sidebarState === 'edit' || sidebarState === 'duplicate' ? (
        <WorkFormSidebar
          isSidebarOpen={true}
          closeSidebar={() => setSidebarState(null)}
          companies={companies}
          formMode={sidebarState}
          selectBorrowerCompany={selectBorrowerCompany}
          ownCompanyId={selectBorrowerCompany ? ownCompany.id : undefined}
          selectedShiftIds={selectedShiftIds}
          isHqAdmin={isHqAdmin}
          onSuccess={handleWorkFormSuccess}
        />
      ) : null}
      {sidebarState === 'bulk-shift-creation' ? (
        <BulkShiftCreationSidebar isOpen={true} setIsBarSideBarOpen={() => setSidebarState(null)} />
      ) : null}
    </>
  );
};
